/**
 * 分身「形象素材」上传限制的**单一源**(2026-08-29)。
 *
 * <p>此前 `MAX_VIDEO_MB = 25` 在三个文件里各写了一份(PC 形象库 / PC 创建页 / 企微 H5),
 * 而时长压根没有判据 —— 只有一句「建议 10~15 秒」的文案。用户要求把上传时长上限对齐
 * 阿里 videotalk 的 2 分钟,如果照旧散着改,必然漏掉一处、让失败往下挪一层。这里收口。
 *
 * <p>★两条限制是**互相独立**的,不能合并成一句「文件不合格」:
 *   · 大小超了 → 用户要去压缩 / 降画质;
 *   · 时长超了 → 用户要去剪短。
 *   说错方向,用户就会把一个 30 秒的 4K 片反复剪短,而问题其实是码率。
 */

/**
 * 源视频时长上限的**回落值**(秒):2 分钟 —— 老出片通道按源片秒数计费的口径。
 * 1020 起真实上限按出片通道从服务端取(model/dhVideoLimits `useDhVideoLimits`,新通道 5 分钟),
 * 本常量只在取不到时兜底。
 */
export const MAX_AVATAR_VIDEO_SECONDS = 120;

/**
 * 源视频大小上限(MB)。
 *
 * ★**这个数由基建决定,不是产品选的** —— 它必须与 prod nginx `/api/` 的
 * `client_max_body_size` **同值**:前端放得比它大 = 把一个 413 白页留给用户,放得比它小 = 白白拒收。
 *
 * ★0829 25 → **100**(副B `fec4c47a` 同批:仓内 `nginx.conf`、真机 nginx、
 * 后端 `AvatarProfileServiceImpl` + Spring multipart 一起放开,api `c14d54c8`)。
 * **为什么是 100 不是 200**:副B 实测这台机器的三条硬上界 —— 容器无内存限制 / 总内存 7G 无 swap /
 * backend 未显式 `-Xmx` 而上传走 `file.getBytes()` **全量入堆**。200MB 会重演
 * 「构建峰值把测试机卡死」那次事故。
 *
 * ★真正的**时长**闸在 ai 侧(1020 起按出片通道取值,经 video-limits 接口下发,见 model/dhVideoLimits)。
 * ★注意体积:5 分钟片按常见手机码率可能逼近/超过 100MB —— 超了按大小闸提示「压缩/调低画质」。
 */
export const MAX_AVATAR_VIDEO_MB = 100;

/** 形象素材合法类型(与后端 avatarExt 对齐)。 */
const PHOTO_TYPES = /^image\/(png|jpeg|webp)$/;
const VIDEO_TYPES = /^video\/(mp4|quicktime)$/;

export function isAvatarVideo(f: File): boolean {
  return VIDEO_TYPES.test(f.type);
}

export function isAvatarPhoto(f: File): boolean {
  return PHOTO_TYPES.test(f.type);
}

/** 超大小 → 一句人话;没超 → null。 */
export function avatarVideoSizeError(f: File): string | null {
  if (f.size <= MAX_AVATAR_VIDEO_MB * 1024 * 1024) return null;
  return `视频过大(超过 ${MAX_AVATAR_VIDEO_MB}MB),请压缩或调低画质后再传`;
}

/**
 * 超时长 → 一句人话;没超 → null。
 *
 * ★读不到时长(NaN / 0 / Infinity / 负数)一律**放行**:浏览器读 metadata 失败是**量具故障**,
 * 把它算成用户的错就成了「明明合规却传不上去」。真正的兜底在后端与上游,
 * 前端这层只在「确实读到了、且确实超了」时才拦。
 */
export function avatarVideoDurationError(
  seconds: number,
  limits: { minSourceSec?: number; maxSourceSec: number } = {
    maxSourceSec: MAX_AVATAR_VIDEO_SECONDS,
  }
): string | null {
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const min = limits.minSourceSec ?? 0;
  if (min > 0 && seconds < min) {
    return `视频太短(当前约 ${Math.round(seconds)} 秒),至少要 ${min} 秒,请换一段正脸清晰的视频`;
  }
  if (seconds <= limits.maxSourceSec) return null;
  const max = limits.maxSourceSec;
  const label = max % 60 === 0 ? `${max / 60} 分钟` : `${max} 秒`;
  return `视频最长 ${label}(当前约 ${Math.round(seconds)} 秒),请剪短后再传`;
}

/**
 * 源视频边长范围(像素)。**宽和高各自**都要落在此区间 —— 阿里 videoretalk 官方口径
 * 「编码采用 H.264 或 H.265;边长不低于 640,不大于 2048」(0908 亲核 API 文档;
 * 此前没有前置闸,超界的片要等出片阶段才报 InvalidFile.Resolution,白等十分钟)。
 */
export const MIN_AVATAR_VIDEO_EDGE = 640;
export const MAX_AVATAR_VIDEO_EDGE = 2048;

/**
 * 边长超界 → 一句人话(说清是哪条边、往哪个方向改);合规/读不到 → null。
 * 读不到(0/NaN)放行:量具故障不算用户的错,后端与上游仍有兜底 —— 与时长闸同哲学。
 */
export function avatarVideoResolutionError(width: number, height: number): string | null {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  const lo = Math.min(width, height);
  const hi = Math.max(width, height);
  if (lo < MIN_AVATAR_VIDEO_EDGE) {
    return `视频分辨率过低(当前 ${width}×${height},最短边需 ≥${MIN_AVATAR_VIDEO_EDGE} 像素),请换更清晰的原片`;
  }
  if (hi > MAX_AVATAR_VIDEO_EDGE) {
    return `视频分辨率过高(当前 ${width}×${height},最长边需 ≤${MAX_AVATAR_VIDEO_EDGE} 像素),请导出成 1080P 后再传`;
  }
  return null;
}

export interface VideoMeta {
  seconds: number;
  width: number;
  height: number;
}

/**
 * 读一个视频文件的时长与宽高。读不到回 NaN/0 —— 交给两个 *Error 判据放行。
 * 单独抽出来是因为它是 DOM 侧效果,纯判据留在上面好测。
 */
/**
 * 探不出元数据时的回落值:NaN 秒 / 0×0 —— 调用方的闸一律按「读不出就不拦」处理
 * (真正的校验在服务端与 ai 侧 B4,前端这道只是省一次往返)。
 */
const UNKNOWN_META: VideoMeta = { seconds: Number.NaN, width: 0, height: 0 };

/**
 * 元数据探测硬超时(毫秒)。★没有它这个 Promise 可能**永不 resolve**:
 * `loadedmetadata` 与 `error` 两个事件只要一个都不来(容器损坏、编解码器缺失、
 * 某些浏览器对 blob: 的边角行为),`await readVideoMeta(...)` 就挂在那 ——
 * 上层是「选完文件 → 体检 → 上传」的串行流,于是表现为**用户选了文件,什么都没发生**,
 * 无报错无日志。本仓反复踩过的"静默"形态,不能再多一处。
 */
const META_PROBE_TIMEOUT_MS = 15_000;

export function readVideoMeta(file: File): Promise<VideoMeta> {
  return new Promise((resolve) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (meta: VideoMeta, url?: string) => {
      if (settled) return;
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
      resolve(meta);
    };
    try {
      const url = URL.createObjectURL(file);
      const v = document.createElement("video");
      v.preload = "metadata";
      v.onloadedmetadata = () =>
        finish({ seconds: v.duration, width: v.videoWidth, height: v.videoHeight }, url);
      v.onerror = () => finish(UNKNOWN_META, url);
      timer = setTimeout(() => finish(UNKNOWN_META, url), META_PROBE_TIMEOUT_MS);
      v.src = url;
    } catch {
      finish(UNKNOWN_META);
    }
  });
}

/** @deprecated 用 {@link readVideoMeta}(分辨率闸 0908 加入后,单读时长的调用方都该顺路拿宽高)。 */
export function readVideoDurationSec(file: File): Promise<number> {
  return readVideoMeta(file).then((m) => m.seconds);
}

/**
 * 分身口播视频的一站式前置体检(1030):大小 → 时长 → 分辨率,命中第一条就回人话;合格 → null。
 *
 * <p>分身创建页之外,人物详情页「分身口播素材」区也能直接传视频 —— 那条口此前**一道闸都没有**,
 * 不合格的片要等出片才失败(1030 测试机爱民三单)。两处共用这一个函数,别再各写各的。
 * ★帧率/编码浏览器读不到,由后端上传体检(ai source-check)兜;这里读不到的项照旧放行。
 */
export async function dhSourceVideoError(
  file: File,
  limits: { minSourceSec?: number; maxSourceSec: number }
): Promise<string | null> {
  const sizeErr = avatarVideoSizeError(file);
  if (sizeErr) return sizeErr;
  const meta = await readVideoMeta(file);
  return (
    avatarVideoDurationError(meta.seconds, limits) ??
    avatarVideoResolutionError(meta.width, meta.height)
  );
}
